import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ControlContainer, FormBuilder, FormGroup, FormGroupDirective, NgForm } from '@angular/forms';
import { Subject, merge } from 'rxjs';
import { debounceTime, filter, startWith, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ExternalPoint, Channel, Locale, LocaleBaseConfig, MultiLocaleFormData } from '@model';
import { ExternalPointFormModel, Interval, Provider } from 'src/app/shared/model/external-point.model';
import { mapPointToFormMapper } from 'src/app/shared/mappers/external-point-form.mapper';
import { buildExternalPointForm } from './external-point-form.builder';
import { SourceIdErrorStateMatcher } from 'src/app/shared/matchers/source-id-error.state-matcher';
import { validateLocaleChanges, applyLocaleValidationResult } from 'src/app/shared/helper';

@Component({
  selector: 'app-external-point-form',
  templateUrl: './external-point-form.component.html',
  styleUrls: ['./external-point-form.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExternalPointFormComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input({ required: true }) point!: ExternalPoint;
  @Input({ required: true }) localeConfig!: LocaleBaseConfig;
  @Input({ required: true }) mode!: 'create' | 'edit';

  @Input() hasError = false;
  @Input() providers: Provider[] = [];
  @Input() channels: Channel[] = [];
  @Input() reportingIntervals: Interval[] = [];
  @Input() userLocale: string = 'en_US';
  @Input() supportedLocales: Locale[] = [];
  @Input() loading = { providers: false, channels: false, intervals: false };
  @Input() stableKey!: string;
  @Input() getServiceDeliveryPointDisplayLabels!: string;
  @Input() getSelectedChannelLabel!: string;

  @Input() sdpDisplayLabelsByLocale: MultiLocaleFormData = {};

  private _index!: number;
  @Input() set index(i: number) {
    this._index = i;
  }
  get index() {
    return this._index;
  }

  @Output() dataProviderChange = new EventEmitter<string>();
  @Output() channelChange = new EventEmitter<string>();
  @Output() formReady = new EventEmitter<FormGroup>();
  @Output() formValue = new EventEmitter<ExternalPointFormModel>();
  @Output() formValidity = new EventEmitter<boolean>();
  @Output() formChange = new EventEmitter<{ index: number; form: FormGroup }>();
  @Output() panelOpenedChanged = new EventEmitter<boolean>();
  externalPointsForm!: FormGroup;
  sourceIdErrorMatcher = new SourceIdErrorStateMatcher();

  nameLabel = '';
  namePlaceholder = '';
  displayLabelsId = 'external_point_display_labels';

  private destroy$ = new Subject<void>();
  private suppressEmit = false;

  private labelsInitializedForPoint = false;
  private lastPointId: string | null | undefined = undefined;
  private previousLocalesCount = 0;

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private i18n: TranslateService,
    private form: NgForm,
  ) {
    this.nameLabel = this.i18n.instant('utility.account.service_delivery_point_name');
    this.namePlaceholder = this.i18n.instant('utility.account.create.placeholder.service_delivery_point_name');
  }

  get valid(): boolean {
    return this.externalPointsForm?.valid ?? false;
  }

  getDisplayLabelsId(): string {
    const key = this.stableKey ?? (this.point as any)?.id ?? (this.point as any)?.tempId ?? 'fallback';
    return `${this.displayLabelsId}_${key}`;
  }

  ngOnInit(): void {
    if (this.localeConfig && !this.externalPointsForm) {
      this.initFormOnce();
    }
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['index'] && !changes['index'].firstChange) {
      this.formChange.emit({ index: this.index, form: this.externalPointsForm });
    }

    if (changes['localeConfig'] && this.localeConfig && !this.externalPointsForm) {
      this.initFormOnce();
    }

    if (changes['point']) {
      const newId = this.point?.id ?? null;
      if (newId !== this.lastPointId) {
        this.labelsInitializedForPoint = false;
        this.lastPointId = newId;
      }
    }
    if (changes['supportedLocales'] && !changes['supportedLocales'].firstChange && this.externalPointsForm) {
      const currentLocales = changes['supportedLocales'].currentValue as Locale[];
      const previousLocales = (changes['supportedLocales'].previousValue as Locale[]) || [];

      const multiLocaleControl =
        this.form.controls[`localeInputsForm_external_point_display_labels_${this.point.id ?? this.stableKey}`];
      const displayLabelsControl = this.externalPointsForm.get('displayLabels');

      if (multiLocaleControl && displayLabelsControl) {
        const displayLabelsValue = displayLabelsControl.value || {};
        const validationResult = validateLocaleChanges(currentLocales, previousLocales, displayLabelsValue);

        applyLocaleValidationResult(validationResult, multiLocaleControl, displayLabelsControl);

        this.panelOpenedChanged.emit(validationResult.shouldExpandPanel);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initFormOnce(): void {
    this.buildForm();

    this.recomputeAndPatch();

    this.formChange.emit({ index: this.index, form: this.externalPointsForm });
    this.formReady.emit(this.externalPointsForm);

    const stream$ = merge(this.externalPointsForm.valueChanges, this.externalPointsForm.statusChanges).pipe(
      debounceTime(120),
      takeUntil(this.destroy$),
      filter(() => !this.suppressEmit),
    );

    stream$.subscribe(() => {
      this.formValue.emit(this.externalPointsForm.getRawValue() as ExternalPointFormModel);
      this.formValidity.emit(this.externalPointsForm.valid);
      this.formChange.emit({ index: this.index, form: this.externalPointsForm });
    });
  }

  private buildForm(): void {
    this.externalPointsForm = buildExternalPointForm(this.fb);
  }

  private recomputeAndPatch(): void {
    const mapped: ExternalPointFormModel = mapPointToFormMapper(
      (this.point ?? ({} as ExternalPoint)) as ExternalPoint,
      this.reportingIntervals ?? [],
      this.sdpDisplayLabelsByLocale ?? {},
      this.channels,
    );

    const isNew = !this.point || this.point.id == null;

    if (isNew) {
      this.safePatch(mapped);
      this.onChannelSelection(mapped.channelId);
    } else {
      if (!this.labelsInitializedForPoint) {
        this.safePatch(mapped);
        this.labelsInitializedForPoint = true;
      } else {
        const { displayLabels, ...rest } = mapped;
        this.safePatch(rest as Partial<ExternalPointFormModel>);
      }
    }
  }

  private safePatch(value: Partial<ExternalPointFormModel>): void {
    if (!this.externalPointsForm) return;
    const wasPristine = this.externalPointsForm.pristine;
    const wasUntouched = this.externalPointsForm.untouched;

    this.suppressEmit = true;

    this.externalPointsForm.patchValue(value as ExternalPointFormModel, { emitEvent: false });
    this.externalPointsForm.updateValueAndValidity({ emitEvent: false });

    if (wasPristine) {
      this.externalPointsForm.markAsPristine();
    }
    if (wasUntouched) {
      this.externalPointsForm.markAsUntouched();
    }

    this.formValue.emit(this.externalPointsForm.getRawValue() as ExternalPointFormModel);
    this.formValidity.emit(this.externalPointsForm.valid);

    this.suppressEmit = false;
    this.cdr.markForCheck();
  }

  onInputUpdate(): void {
    this.externalPointsForm.get('displayLabels')?.updateValueAndValidity({ onlySelf: true });
    this.cdr.markForCheck();
  }

  onProviderSelection(id: string): void {
    this.dataProviderChange.emit(id);
  }

  onChannelSelection(id: string): void {
    const selectedChannel = this.channels.find((c) => c.id === id);
    if (selectedChannel) {
      this.channelChange.emit(id);
      const currentValue = this.externalPointsForm.value as ExternalPointFormModel;
      this.safePatch({ ...currentValue, channelId: id });
    }
  }

  markAllAsTouched(): void {
    this.externalPointsForm?.markAllAsTouched();
    this.cdr.markForCheck();
  }
}
